import { useCallback, useEffect, useState } from "react";
import { Copy } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

const STATUS = { pendente: "Pendente", ativado: "Link aberto", em_andamento: "Em andamento", concluido: "Concluído", revogado: "Revogado" };
const fmt = (d) => (d ? new Date(d).toLocaleDateString("pt-BR") : "—");

const copy = async (link) => {
  try {
    await navigator.clipboard.writeText(link);
    toast.success("Link copiado.");
  } catch {
    window.prompt("Copie o link:", link);
  }
};

const InviteRow = ({ inv, onLink, onRevoke }) => (
  <li className="rounded-2xl border bg-white p-4 text-sm" data-testid={`invite-${inv.id}`}>
    <div className="flex items-start justify-between gap-3">
      <div>
        <b className="text-stone-900">{inv.name}</b>
        <p className="text-stone-500">{inv.email}</p>
      </div>
      <span className="whitespace-nowrap rounded-full bg-[#f5efe6] px-3 py-1 text-xs" data-testid={`invite-${inv.id}-status`}>{STATUS[inv.status]}</span>
    </div>
    <p className="mt-2 text-xs text-stone-500">Criado {fmt(inv.created_at)} · Aberto {fmt(inv.activated_at)} · Concluído {fmt(inv.completed_at)}</p>
    {inv.status !== "revogado" && (
      <div className="mt-3 flex gap-2">
        <button className="btn-ghost !min-h-[40px] border text-xs" onClick={() => onLink(inv)} data-testid={`invite-${inv.id}-newlink`}>Gerar novo link</button>
        <button className="btn-ghost !min-h-[40px] border text-xs text-[#8a3f22]" onClick={() => onRevoke(inv)} data-testid={`invite-${inv.id}-revoke`}>Revogar</button>
      </div>
    )}
  </li>
);

export default function AdminInvites({ adminKey }) {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ name: "", email: "" });
  const [link, setLink] = useState("");
  const headers = { "X-Admin-Key": adminKey };

  const load = useCallback(() => api.get("/admin/invites", { headers: { "X-Admin-Key": adminKey } }).then((r) => setList(r.data)).catch((e) => toast.error(errMsg(e))), [adminKey]);
  useEffect(() => {
    load();
  }, [load]);

  const create = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post("/admin/invites", form, { headers });
      setLink(r.data.link);
      setForm({ name: "", email: "" });
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  const newLink = async (inv) => {
    if (!window.confirm(`Gerar novo link para ${inv.name}? O link anterior deixará de funcionar (respostas e relatório são mantidos).`)) return;
    try {
      const r = await api.post(`/admin/invites/${inv.id}/link`, {}, { headers });
      setLink(r.data.link);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  const revoke = async (inv) => {
    if (!window.confirm(`Revogar o convite de ${inv.name}? O acesso será bloqueado.`)) return;
    try {
      await api.post(`/admin/invites/${inv.id}/revoke`, {}, { headers });
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <section className="mt-12" data-testid="admin-invites">
      <h2 className="text-2xl text-stone-900">Convites de cortesia</h2>
      <p className="mt-2 text-sm text-stone-600">Acesso gratuito e individual à experiência completa. Cada convite gera uma única jornada e um único relatório.</p>
      <form onSubmit={create} className="mt-4 space-y-2">
        <input required className="field" placeholder="Nome do convidado" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="invite-name-input" />
        <input required type="email" className="field" placeholder="E-mail do convidado" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="invite-email-input" />
        <button className="btn-primary w-full" data-testid="invite-create-button">Gerar convite</button>
      </form>
      {link && (
        <div className="mt-4 rounded-2xl border border-[#c88a2e] bg-[#fbf6ee] p-4" data-testid="invite-link-box">
          <p className="text-xs text-stone-600">Link individual (copie agora; por segurança ele não fica salvo para exibição posterior):</p>
          <p className="mt-2 break-all text-sm text-stone-900" data-testid="invite-link">{link}</p>
          <button className="btn-ghost mt-2 border" onClick={() => copy(link)} data-testid="invite-copy-button"><Copy size={16} /> Copiar link</button>
        </div>
      )}
      <ul className="mt-6 space-y-3">{list.map((inv) => <InviteRow key={inv.id} inv={inv} onLink={newLink} onRevoke={revoke} />)}</ul>
    </section>
  );
}
